import type { ReactNode } from 'react';

import { ListItemText, Skeleton, Typography, useTheme } from '@mui/material';
import { Stack } from '@mui/system';

import RouterLink from '~components/buttons/routerLink';
import FramedIcon from '~components/icons/framedIcon';
import VectorImage, { type VectorImageName } from '~components/media/vectorImage';
import Tooltip from '~components/tooltips/tooltip';
import { StatusVariant } from '~enums';
import { Dimens } from '~themes';

/**
 * A simple component to show two lines of text, a primary and secondary line.
 */
interface TwoLineItemProps {
	primary: string;
	tooltipText?: string | undefined;
	secondary?: string | ReactNode;
	secondarySize?: 'normal' | 'large';
	link?: string;
	linkTarget?: string;
	linkSearch?: string;
	icon?: VectorImageName | undefined;
	isFramedIcon?: boolean;
	isLoading?: boolean;
	framedIconVariant?: StatusVariant;
}

const TwoLineItem = ({
	primary,
	tooltipText,
	secondary,
	secondarySize = 'normal',
	link,
	linkTarget,
	linkSearch,
	icon,
	isFramedIcon = true,
	isLoading = false,
	framedIconVariant = StatusVariant.Neutral,
}: TwoLineItemProps) => {
	const { palette } = useTheme();

	const primaryContent =
		isLoading ? <Skeleton width={'100px'} />
		: !tooltipText ?
			<Typography variant='paragraph' color={palette.textQuaternary.main} component='p'>
				{primary}
			</Typography>
		:	<Stack direction='row' spacing={Dimens.spacingXs} alignItems='center'>
				<Typography variant='paragraph' color={palette.textQuaternary.main} component='p'>
					{primary}
				</Typography>
				<Tooltip content={tooltipText} placement='top'>
					<VectorImage
						name='icon/info-circle'
						color={palette.borderPrimarySolid.main}
						size={Dimens.spacingXl}
					/>
				</Tooltip>
			</Stack>;

	const secondaryVariant = secondarySize === 'large' ? 'labelLg' : 'label';

	const secondaryContent =
		isLoading ? <Skeleton width={'120px'} />
		: link && secondary != null ?
			<Typography variant={secondaryVariant} color={palette.textPrimary.main} component='p'>
				{linkTarget ?
					<a
						href={link}
						target={linkTarget ?? '_blank'}
						rel='noopener noreferrer'
						style={{ color: 'inherit', textDecoration: 'underline' }}
					>
						{secondary}
					</a>
				:	<RouterLink
						to={{
							pathname: link,
							search: linkSearch,
						}}
					>
						<Typography
							variant={secondaryVariant}
							color={palette.textPrimary.main}
							component='span'
						>
							{secondary}
						</Typography>
					</RouterLink>
				}
			</Typography>
		: typeof secondary == 'string' ?
			<Typography variant={secondaryVariant} color={palette.textPrimary.main} component='p'>
				{secondary}
			</Typography>
		:	secondary;

	if (icon) {
		return (
			<Stack direction={'row'} alignItems={'center'} spacing={Dimens.spacingLg}>
				{isFramedIcon ?
					<FramedIcon name={icon} variant={framedIconVariant} />
				:	<VectorImage
						name={icon}
						height={Dimens.spacing6xl}
						width={Dimens.spacing6xl}
						style={{ padding: Dimens.spacingLg }}
					/>
				}
				<ListItemText primary={primaryContent} secondary={secondaryContent} />
			</Stack>
		);
	}
	return <ListItemText primary={primaryContent} secondary={secondaryContent} />;
};

export default TwoLineItem;
